import { GBP_LIMITS } from './google-ranking-constants';
import type { GbpSuggestionKind } from './google-ranking-enums';

/**
 * Google profile score (v2): 100 points in 4 groups, weighted by what moves Google Maps ranking the most
 * (category, then reviews, then hours). Every item is graded 0..maxPoints (integers), so score = Σ item
 * points and each group max is fixed. The API grades with these numbers and the web's "How your score
 * works" panel renders straight from them, so the two can never drift.
 */

/**
 * Stored on the snapshot and on each score day: scores of different versions are never compared.
 * Grading per business kind (hair salons, restaurants…) kept v2: those listings were never graded before
 * (the Google tools were nail-only), nail grading is unchanged, and a nail salon never has a not-applicable
 * item, so its total is still the plain sum of 100 points. No stored score changed meaning.
 */
export const GBP_SCORE_VERSION = 2;

export const GBP_SCORE_GROUP_KEYS = ['FOUND', 'REVIEWS', 'PHOTOS_POSTS', 'INFO_BOOKING'] as const;
export type GbpScoreGroupKey = (typeof GBP_SCORE_GROUP_KEYS)[number];

export const GBP_SCORE_GROUPS: Record<GbpScoreGroupKey, { maxPoints: number }> = {
  FOUND: { maxPoints: 35 },
  REVIEWS: { maxPoints: 35 },
  PHOTOS_POSTS: { maxPoints: 15 },
  INFO_BOOKING: { maxPoints: 15 },
};

/** Fixed display order: group by group, biggest lever first. */
export const GBP_SCORE_ITEM_KEYS = [
  'main_category',
  'extra_categories',
  'services_coverage',
  'services_priced',
  'website_link',
  'reviews_total',
  'reviews_pace',
  'star_rating',
  'review_replies',
  'photos_recent',
  'photo_kinds',
  'photos_total',
  'post_recent',
  'hours_match',
  'holiday_hours',
  'booking_link',
  'description_quality',
] as const;
export type GbpScoreItemKey = (typeof GBP_SCORE_ITEM_KEYS)[number];

/** Shortcuts for gaps Hayven cannot send to Google itself (the owner's turn). */
export const GBP_OWNER_ACTION_KEYS = [
  'REVIEW_REQUESTS',
  'REVIEW_REPLIES',
  'PHOTO_SEND_NOW',
  'PHOTO_UPLOAD',
  'POSTS',
  'HOLIDAY_ANSWER',
] as const;
export type GbpOwnerActionKey = (typeof GBP_OWNER_ACTION_KEYS)[number];

export interface GbpScoreItemMeta {
  group: GbpScoreGroupKey;
  maxPoints: number;
  /** Suggestion kind that closes the gap once the owner approves it. */
  fixedBy: GbpSuggestionKind | null;
  /** Owner shortcut for the gap (an item may have both, e.g. holiday hours). */
  ownerAction: GbpOwnerActionKey | null;
}

/**
 * Stored items keep the `maxPoints` they were graded with, so changing any weight below (or a grading rule)
 * means a new `GBP_SCORE_VERSION`; otherwise old and new scores would be compared as equals.
 */
const item = (
  group: GbpScoreGroupKey,
  maxPoints: number,
  fixedBy: GbpSuggestionKind | null,
  ownerAction: GbpOwnerActionKey | null = null
): GbpScoreItemMeta => ({ group, maxPoints, fixedBy, ownerAction });

export const GBP_SCORE_ITEMS: Record<GbpScoreItemKey, GbpScoreItemMeta> = {
  main_category: item('FOUND', 15, 'PRIMARY_CATEGORY'),
  extra_categories: item('FOUND', 5, 'ADDITIONAL_CATEGORIES'),
  services_coverage: item('FOUND', 6, 'SERVICE_ITEMS'),
  services_priced: item('FOUND', 3, 'SERVICE_ITEMS'),
  website_link: item('FOUND', 6, 'WEBSITE_URI'),
  reviews_total: item('REVIEWS', 10, null, 'REVIEW_REQUESTS'),
  reviews_pace: item('REVIEWS', 10, null, 'REVIEW_REQUESTS'),
  // More reviews from happy clients is the only lever on the average.
  star_rating: item('REVIEWS', 8, null, 'REVIEW_REQUESTS'),
  review_replies: item('REVIEWS', 7, null, 'REVIEW_REPLIES'),
  photos_recent: item('PHOTOS_POSTS', 5, null, 'PHOTO_SEND_NOW'),
  photo_kinds: item('PHOTOS_POSTS', 4, null, 'PHOTO_UPLOAD'),
  photos_total: item('PHOTOS_POSTS', 3, null, 'PHOTO_SEND_NOW'),
  post_recent: item('PHOTOS_POSTS', 3, null, 'POSTS'),
  hours_match: item('INFO_BOOKING', 6, 'REGULAR_HOURS'),
  holiday_hours: item('INFO_BOOKING', 2, 'SPECIAL_HOURS', 'HOLIDAY_ANSWER'),
  booking_link: item('INFO_BOOKING', 5, 'BOOKING_LINK'),
  description_quality: item('INFO_BOOKING', 2, 'DESCRIPTION'),
};

/** Where a benchmark number comes from: the local top 3 of the weekly map scan, or typical numbers. */
export const GBP_BENCHMARK_SOURCES = ['TOP3', 'TYPICAL'] as const;
export type GbpBenchmarkSource = (typeof GBP_BENCHMARK_SOURCES)[number];
export type GbpBenchmarkMetric = { value: number; source: GbpBenchmarkSource };

/**
 * Typical numbers for a busy nail salon, used per metric until the weekly scan has top-3 data (the web
 * then says "busy nail salons usually…"). Tunable. Other kinds: `BUSINESS_KIND_PROFILES[kind].typicalBenchmark`
 * (@repo/shared/business-kind).
 */
export const GBP_BENCHMARK_TYPICAL = { reviewsTotal: 250, reviewsPer30d: 10, photosTotal: 150 } as const;

/** Every grading threshold in one block, so tuning is one edit. */
export const GBP_SCORE_THRESHOLDS = {
  /** Menu services matching an extra category before it is wanted on its own. */
  extraCategoryMenuMin: 2,
  /** With top-3 backing, this many matching menu services already make the category wanted… */
  extraCategoryBackedMenuMin: 1,
  /** …when at least this many of the top 3 list it. */
  extraCategoryBackedTop3Min: 2,
  /** Without a Hayven menu, this many services on Google earn the full services points. */
  servicesWithoutMenuFullAt: 10,
  /** Missing menu services named in the facts (for "Hayven adds Dip powder, Gel-X…"). */
  missingServicesSample: 6,
  /** A social profile or link page as the website earns this much (own site / Bio Page = full). */
  websiteLinkWeakPoints: 2,
  /** Star rating at or below `starRatingFloor` earns 0, at or above `starRatingFull` the full points. */
  starRatingFloor: 4.0,
  starRatingFull: 4.8,
  /** Reply share counts over this window; review stats count new reviews over `recentDays`. */
  replyRateWindowDays: 90,
  recentDays: 30,
  /** Replies: reply share × `replyRatePoints`, the rest of the item when no low-star review is waiting. */
  replyRatePoints: 5,
  /** Low-star reviews (1..this) waiting for a reply cost the reply bonus. */
  lowStarMax: 3,
  waitingLowStarSample: 3,
  /** New owner photos in `recentDays`: at least `photosRecentFull` → full points, at least 1 → `photosRecentSomePoints`. */
  photosRecentFull: 3,
  photosRecentSomePoints: 3,
  postRecentDays: 14,
  /** Holidays this many days ahead (today included) must have an answer. */
  holidayWindowDays: 42,
  /** A clean description fills what Google shows before "More". */
  descriptionCleanMinChars: GBP_LIMITS.descriptionVisibleChars,
} as const;
